import { createApi } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';
import {
  DeviceDetails,
  IGetCredentialsResponse,
  IMethodsInfoByDeviceId,
  IPostDebugDevice,
  IPostSingletonStaticMethod,
  IGlobalDeviceAnnounceItem,
  ILastDeviceAnnouncePayloadResponse,
  MethodDevice,
  MoveDevice,
  NewDevice,
  RegisteredDevice,
  TelemetryDevice,
  TelemetryDeviceGet,
  IDeviceAnnounceByDeviceId,
  IRecordsSyncStatus,
  ISetWifiCredentialsPayload,
  IDetailsRecordDbData,
  IFileDevice,
  IDealerDeviceStateCount
} from '../../types/Device/DeviceInterfaces';
import { Folder } from '../../types/folders/folder';
import customFetchBase from '../expiredToken/expiredToken';
import {
  AddLocationInOrg,
  Location,
  LocationAssign,
  LocationAssignToOrganization,
  LocationByUser,
  LocationInOrganization,
  LocationNew,
  LocationSelected,
  LocationUserSelected,
  TransferLocationWithEmail
} from '../../types/location/location';
import {
  AddBusinessAccount,
  AddBusinessAccountUser,
  BusinessAccount,
  BusinessAccountUser,
  EditBusinessAccount
} from '../../types/businessAccount/businessAccount';
import {
  Organization,
  TransferOrganizationWithEmail
} from '../../types/organizations/organizations';
import { UsersByLocation } from '../../types/users';

const customFetch = customFetchBase(config.baseUrlCustomers);

export const apiDevices = createApi({
  baseQuery: customFetch,
  endpoints: (builder) => ({
    getDeviceMethods: builder.mutation<IMethodsInfoByDeviceId[], string>({
      query: (deviceId) => ({
        url: `api/Device/DeviceMethodsByDeviceId?deviceId=${deviceId}`,
        method: 'GET'
      })
    }),
    addDevice: builder.mutation<{}, NewDevice>({
      query: (data) => ({
        url: 'api/RegisterDevice',
        method: 'POST',
        body: {
          deviceId: data.deviceId,
          productId: +data.productName
        }
      })
    }),
    getCredentials: builder.mutation<string[], string>({
      query: (deviceId) => ({
        url: `api/Device/GetCredentials/${deviceId}`,
        method: 'GET'
      })
    }),
    deleteDevice: builder.mutation<{}, string[]>({
      query: (deviceIds) => ({
        url: `/api/Device/RegisteredDevice?ids=${deviceIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    getDevices: builder.mutation<RegisteredDevice[], {}>({
      query: () => ({
        url: '/api/Device/RegisteredDevices',
        method: 'GET',
        headers: { 'Content-Type': 'application/json' }
      })
    }),
    getDebugDevice: builder.mutation<[], string>({
      query: (deviceId) => ({
        url: `api/Device/Debug/${deviceId}`,
        method: 'GET'
      })
    }),
    postStaticMethod: builder.mutation<any, {}>({
      query: (data) => ({
        url: 'api/Device/DeviceStaticMethod',
        method: 'POST',
        body: data
      })
    }),
    postSingletonMethod: builder.mutation<any, IPostSingletonStaticMethod[]>({
      query: (data) => ({
        url: 'api/Device/DeviceSingletonMethod',
        method: 'POST',
        body: data
      })
    }),
    updateMethodsOrder: builder.mutation<
      any,
      { data: IPostSingletonStaticMethod[]; type: string }
    >({
      query: (data) => ({
        url: `api/Device/Device${data.type}Method`,
        method: 'PUT',
        body: data.data
      })
    }),
    deleteStaticMethod: builder.mutation<any, number>({
      query: (deviceId) => ({
        url: `api/Device/DeviceStaticMethod/${deviceId}`,
        method: 'DELETE'
      })
    }),
    deleteSingletonMethod: builder.mutation<any, number>({
      query: (deviceId) => ({
        url: `api/Device/DeviceSingletonMethod/${deviceId}`,
        method: 'DELETE'
      })
    }),
    getDeviceAnnounce: builder.mutation<IDeviceAnnounceByDeviceId[], string>({
      query: (deviceId) => ({
        url: `api/Device/DeviceAnnounce/${deviceId}`,
        method: 'GET'
      })
    }),
    getLocationsByLoggedUser: builder.mutation<LocationByUser[], {}>({
      query: () => ({
        url: `api/Location/GetLoggedUserLocations`,
        method: 'GET'
      })
    }),
    getLocations: builder.mutation<Location[], {}>({
      query: () => ({
        url: 'api/Location/AllLocations',
        method: 'GET'
      })
    }),
    getFoldersTree: builder.mutation<Folder[], {}>({
      query: () => ({
        url: 'api/Folder/UserFolderTrees',
        method: 'GET'
      })
    }),
    getFoldersUserTree: builder.mutation<Folder[], string>({
      query: (userId) => ({
        url: `api/Folder/${userId}/UserFolderTrees`,
        method: 'GET'
      })
    }),
    postFolders: builder.mutation<
      [],
      {
        Name: string;
        ParentId: number | null;
      }
    >({
      query: (data) => ({
        url: `api/Folder`,
        method: 'POST',
        body: data
      })
    }),
    postLocation: builder.mutation<[], LocationNew>({
      query: (data) => ({
        url: `api/Location`,
        method: 'POST',
        body: data
      })
    }),
    putLocation: builder.mutation<[], LocationNew>({
      query: (data) => ({
        url: `api/Location`,
        method: 'PUT',
        body: data
      })
    }),
    assignLocationToFolder: builder.mutation<[], LocationAssign>({
      query: (data) => ({
        url: `/api/Folder/FolderLocation`,
        method: 'POST',
        body: data
      })
    }),
    getOrganizations: builder.mutation<Organization[], string>({
      query: () => ({
        url: `/api/Organization/OrganizationsSummary`,
        method: 'GET'
      })
    }),
    postOrganization: builder.mutation<
      number,
      {
        Name: string;
        ParentId: number | null;
        OwnerId: number | null;
      }
    >({
      query: (data) => ({
        url: `api/Organization`,
        method: 'POST',
        body: data
      })
    }),
    deleteOrganization: builder.mutation<any, number[]>({
      query: (organizationIds) => ({
        url: `/api/Organization?ids=${organizationIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    assignLocationToOrganization: builder.mutation<
      any,
      LocationAssignToOrganization
    >({
      query: (data) => ({
        url: `/api/Organization/OrganizationLocation`,
        method: 'POST',
        body: data
      })
    }),
    deleteLocationInOrganization: builder.mutation<any, number>({
      query: (organizationId) => ({
        url: `/api/Organization/OrganizationLocation?organizationLocationId=${organizationId}`,
        method: 'DELETE'
      })
    }),
    getOrganizationLocations: builder.mutation<
      LocationInOrganization[],
      string
    >({
      query: (organizationId) => ({
        url: `/api/Organization/OrganizationLocations/${organizationId}`,
        method: 'GET'
      })
    }),
    transferOrg: builder.mutation<{}, TransferOrganizationWithEmail>({
      query: (data) => ({
        url: `/api/Organization/TransferOrganizationUsingEmail`,
        method: 'POST',
        body: data
      })
    }),
    transferLoc: builder.mutation<{}, TransferLocationWithEmail>({
      query: (data) => ({
        url: `/api/Location/TransferLocationUsingEmail`,
        method: 'POST',
        body: data
      })
    }),
    ShareOrg: builder.mutation<
      {},
      {
        UserId: number;
        OrganizationRoleId: number;
        OrganizationId: number;
      }
    >({
      query: (data) => ({
        url: `api/Organization/ShareOrganization`,
        method: 'POST',
        body: data
      })
    }),
    ShareLoc: builder.mutation<
      {},
      {
        userId: number;
        locationRoleId: number;
        locationId: number;
      }
    >({
      query: (data) => ({
        url: `api/Location/ShareLocation`,
        method: 'POST',
        body: data
      })
    }),
    getRolesLocation: builder.mutation<{ id: number; name: string }[], string>({
      query: () => ({
        url: `api/LocationPermission/Roles`,
        method: 'GET'
      })
    }),
    getRolesOrganization: builder.mutation<
      { id: number; name: string }[],
      string
    >({
      query: () => ({
        url: `api/OrganizationPermission/Roles`,
        method: 'GET'
      })
    }),
    getLocationsInOrganization: builder.mutation<
      { locations: LocationInOrganization[]; organizations: [] },
      string
    >({
      query: (organizationId) => ({
        url: `/api/Organization/LocationsAndOrganizations/${organizationId}`,
        method: 'GET'
      })
    }),
    getAllBusinessAccount: builder.mutation<BusinessAccount[], string>({
      query: () => ({
        url: `/api/BusinessAccount/AllBusinessAccounts`,
        method: 'GET'
      })
    }),
    getBusinessAccountByUser: builder.mutation<BusinessAccount[], string>({
      query: (idOwner) => ({
        url: `/api/BusinessAccount/${idOwner}`,
        method: 'GET'
      })
    }),
    getBusinessAccountUsers: builder.mutation<BusinessAccountUser[], string>({
      query: (idBusinessAccount) => ({
        url: `/api/BusinessAccountUser/${idBusinessAccount}`,
        method: 'GET'
      })
    }),
    addBusinessAccountUser: builder.mutation<{}, AddBusinessAccountUser>({
      query: (data) => ({
        url: `api/BusinessAccountUser`,
        method: 'POST',
        body: data
      })
    }),
    editBusinessAccountUser: builder.mutation<{}, AddBusinessAccountUser>({
      query: (data) => ({
        url: `api/BusinessAccountUser`,
        method: 'PUT',
        body: data
      })
    }),
    deleteBusinessAccountUser: builder.mutation<{}, string>({
      query: (businessAccountUserId) => ({
        url: `api/BusinessAccountUser/${businessAccountUserId}`,
        method: 'DELETE'
      })
    }),
    editBusinessAccount: builder.mutation<{}, EditBusinessAccount>({
      query: (data) => ({
        url: `/api/BusinessAccount`,
        method: 'PUT',
        body: data
      })
    }),
    deleteBusinessAccount: builder.mutation<{}, string>({
      query: (businessId) => ({
        url: `api/BusinessAccount/${businessId}`,
        method: 'DELETE'
      })
    }),
    userSelectedLocation: builder.mutation<{}, LocationSelected>({
      query: (data) => ({
        url: `/api/Location/UserSelectedLocation`,
        method: 'POST',
        body: data
      })
    }),
    getUserSelectedLocation: builder.mutation<LocationUserSelected[], string>({
      query: (UserId) => ({
        url: `/api/Location/UserSelectedLocation/${UserId}`,
        method: 'GET'
      })
    }),
    getAllProductNames: builder.mutation<
      { id: number; name: string }[],
      string
    >({
      query: () => ({
        url: `/api/Product/AllProducts`,
        method: 'GET'
      })
    }),
    shareOrganizationUsingEmail: builder.mutation<
      {},
      {
        email: string;
        organizationIds: number[];
        organizationRoleId: number;
      }
    >({
      query: (data) => ({
        url: `/api/Organization/ShareOrganizationUsingEmail`,
        method: 'POST',
        body: data
      })
    }),
    shareLocationWithBusinessAccount: builder.mutation<
      {},
      {
        businessAccountTag: string;
        locationRoleId: number;
        locationIds: number[];
      }
    >({
      query: (data) => ({
        url: `/api/Location/ShareLocationWithBusinessAccountUsingTag`,
        method: 'POST',
        body: data
      })
    }),
    shareOrganizationWithBusinessAccount: builder.mutation<
      {},
      {
        businessAccountTag: string;
        organizationRoleId: number;
        organizationIds: number[];
      }
    >({
      query: (data) => ({
        url: `/api/Organization/ShareOrganizationWithBusinessAccountUsingTag`,
        method: 'POST',
        body: data
      })
    }),
    addLocationInOrg: builder.mutation<{}, AddLocationInOrg>({
      query: (data) => ({
        url: `/api/Organization/OrganizationLocation`,
        method: 'POST',
        body: data
      })
    }),
    deleteUserFromLocation: builder.mutation<{}, {}>({
      query: (userLocationId: number) => ({
        url: `api/Location/LocationUser/${userLocationId}`,
        method: 'DELETE'
      })
    }),
    getLocationsUsersByLocationId: builder.mutation<UsersByLocation[], {}>({
      query: (locationId: number) => ({
        url: `api/Location/GetLocationUsersByLocationId/${locationId}`,
        method: 'GET'
      })
    }),
    getRecordsByDb: builder.mutation<IDetailsRecordDbData, TelemetryDeviceGet>({
      query: (data) => ({
        url: `api/Device/Records?deviceId=${data.deviceId}&dbName=${data.dbName}&Page=${data.page}&Size=${data.pageSize}`,
        method: 'GET'
      })
    }),
    getRecordsSyncStatus: builder.mutation<IRecordsSyncStatus[], string>({
      query: (deviceId) => ({
        url: `api/Device/Records/SyncStatus/${deviceId}`,
        method: 'GET'
      })
    }),
    getRecordsDbNames: builder.mutation<string[], string>({
      query: (deviceId) => ({
        url: `api/Device/Records/${deviceId}/DbNames`,
        method: 'GET'
      })
    }),
    getDeviceInfo: builder.mutation<DeviceDetails, string>({
      query: (deviceId) => ({
        url: `api/Device/${deviceId}`,
        method: 'GET'
      })
    }),
    moveSingletonMethod: builder.mutation<{}, MoveDevice[]>({
      query: (data) => ({
        url: `api/Device/ReorderDeviceSingletonMethods`,
        method: 'PUT',
        body: data
      })
    }),
    moveStaticMethod: builder.mutation<{}, MoveDevice[]>({
      query: (data) => ({
        url: `api/Device/ReorderDeviceStaticMethods`,
        method: 'PUT',
        body: data
      })
    }),
    updateStaticMethod: builder.mutation<{}, MethodDevice>({
      query: (data) => ({
        url: `api/Device/DeviceStaticMethod`,
        method: 'PUT',
        body: data
      })
    }),
    updateSingletonMethod: builder.mutation<{}, MethodDevice>({
      query: (data) => ({
        url: `api/Device/DeviceSingletonMethod`,
        method: 'PUT',
        body: data
      })
    }),
    deleteDeviceMethods: builder.mutation<
      {},
      { singletonIds: number[]; staticIds: number[] }
    >({
      query: (data) => ({
        url: `api/Device/DeviceMethod?singletonIds=${data.singletonIds.join(
          ','
        )}&staticIds=${data.staticIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    postCustomFileDevice: builder.mutation<
      {},
      { deviceId: string; data: FormData }
    >({
      query: (data) => ({
        url: `api/TestMessage/CustomFIle/${data.deviceId}`,
        method: 'POST',
        body: data.data
      })
    }),
    deleteDbFromDevice: builder.mutation<
      {},
      { deviceId: string; dbName: string }
    >({
      query: (data) => ({
        url: `api/Device/Records?DeviceId=${data.deviceId}&DBName=${data.dbName}`,
        method: 'DELETE'
      })
    }),
    getGlobalDeviceAnnounce: builder.mutation<
      IGlobalDeviceAnnounceItem[],
      void
    >({
      query: () => ({
        url: `api/Device/GlobalDeviceAnnounceReport`,
        method: 'GET'
      })
    }),
    getLastDeviceAnnounceByDeviceId: builder.mutation<
      ILastDeviceAnnouncePayloadResponse,
      string
    >({
      query: (deviceId) => ({
        url: `api/Device/LastDeviceAnnounce/${deviceId}`,
        method: 'GET'
      })
    }),
    deleteMultipleDeviceMethods: builder.mutation<
      {},
      { singletonIds: number[]; staticIds: number[] }
    >({
      query: (data) => ({
        url: `api/Device/DeviceMethod?singletonIds=${data.singletonIds.join(
          ','
        )}&staticIds=${data.staticIds.join(',')}`,
        method: 'DELETE'
      })
    }),
    getFilesSyncByDeviceId: builder.mutation<any, string>({
      query: (deviceId) => ({
        url: `/api/FilesSync/DesiredProperties/${deviceId}`,
        method: 'GET'
      })
    }),
    toggleSyncStatusRecordDb: builder.mutation<
      {},
      { deviceId: string; dbName: string; toggleText: string }
    >({
      query: (data) => ({
        url: `api/Device/Records/SyncStatus/${data.toggleText}`,
        method: 'POST',
        body: {
          deviceId: data.deviceId,
          dbName: data.dbName
        }
      })
    }),
    getFileSyncDataRetriever: builder.mutation<
      { columns: { name: string; dataType: string }[]; rows: [] }[],
      string
    >({
      query: (url) => ({
        url: `/api/FilesSync/DataRetriever`,
        method: 'POST',
        body: { url }
      })
    }),
    deviceRebootCommand: builder.mutation<{}, string[]>({
      query: (deviceIds) => ({
        url: `api/Commands/Reboot`,
        method: 'POST',
        body: { deviceIds }
      })
    }),
    deviceSetWifiCredentials: builder.mutation<{}, ISetWifiCredentialsPayload>({
      query: (data) => ({
        url: `api/Commands/SetWifiCredentials`,
        method: 'POST',
        body: data
      })
    }),
    getListOfFilesCommandByDeviceId: builder.mutation<IFileDevice[], string>({
      query: (deviceId) => ({
        url: `api/Commands/FileList/${deviceId}`,
        method: 'GET'
      })
    }),
    sendFileThroughEmail: builder.mutation<
      {},
      { deviceId: string; fileNames: string[] }
    >({
      query: (data) => ({
        url: `api/Commands/SendFilesThroughEmail`,
        method: 'POST',
        body: data
      })
    }),
    getDealerDeviceStateCount: builder.mutation<
      IDealerDeviceStateCount[],
      void
    >({
      query: () => ({
        url: `api/Device/DealerDevicesStateCount`,
        method: 'GET'
      })
    }),
    deleteDeviceCredentials: builder.mutation<{}, string>({
      query: (deviceId) => ({
        url: `api/Commands/DeleteCredentials`,
        method: 'POST',
        body: {
          deviceId
        }
      })
    }),
    deleteScheduleFirmwareUpdate: builder.mutation<{}, number[]>({
      query: (data) => ({
        url: `api/Firmware/Schedule?ids=${data.join(',')}`,
        method: 'DELETE',
        body: data
      })
    })
  })
});

export const {
  useAddDeviceMutation,
  useGetDevicesMutation,
  useDeleteDeviceMutation,
  useGetDeviceMethodsMutation,
  useGetDebugDeviceMutation,
  usePostSingletonMethodMutation,
  usePostStaticMethodMutation,
  useDeleteSingletonMethodMutation,
  useDeleteStaticMethodMutation,
  useGetDeviceAnnounceMutation,
  useGetCredentialsMutation,
  useGetLocationsByLoggedUserMutation,
  useGetLocationsMutation,
  useGetFoldersTreeMutation,
  useGetFoldersUserTreeMutation,
  usePostFoldersMutation,
  usePostLocationMutation,
  useAssignLocationToFolderMutation,
  useGetOrganizationsMutation,
  usePostOrganizationMutation,
  useDeleteOrganizationMutation,
  useAssignLocationToOrganizationMutation,
  useDeleteLocationInOrganizationMutation,
  useGetOrganizationLocationsMutation,
  useTransferOrgMutation,
  useTransferLocMutation,
  useShareOrgMutation,
  useShareLocMutation,
  useGetRolesLocationMutation,
  useGetRolesOrganizationMutation,
  useGetLocationsInOrganizationMutation,
  useGetAllBusinessAccountMutation,
  useGetBusinessAccountByUserMutation,
  useGetBusinessAccountUsersMutation,
  useAddBusinessAccountUserMutation,
  useEditBusinessAccountUserMutation,
  useDeleteBusinessAccountUserMutation,
  useDeleteBusinessAccountMutation,
  useUserSelectedLocationMutation,
  useGetUserSelectedLocationMutation,
  useGetAllProductNamesMutation,
  useShareOrganizationUsingEmailMutation,
  useShareLocationWithBusinessAccountMutation,
  useShareOrganizationWithBusinessAccountMutation,
  useEditBusinessAccountMutation,
  useAddLocationInOrgMutation,
  useDeleteUserFromLocationMutation,
  useGetLocationsUsersByLocationIdMutation,
  useGetRecordsByDbMutation,
  usePutLocationMutation,
  useGetRecordsDbNamesMutation,
  useGetDeviceInfoMutation,
  useMoveSingletonMethodMutation,
  useMoveStaticMethodMutation,
  useUpdateSingletonMethodMutation,
  useUpdateStaticMethodMutation,
  useDeleteDeviceMethodsMutation,
  usePostCustomFileDeviceMutation,
  useDeleteDbFromDeviceMutation,
  useGetGlobalDeviceAnnounceMutation,
  useGetLastDeviceAnnounceByDeviceIdMutation,
  useUpdateMethodsOrderMutation,
  useDeleteMultipleDeviceMethodsMutation,
  useGetRecordsSyncStatusMutation,
  useToggleSyncStatusRecordDbMutation,
  useGetFilesSyncByDeviceIdMutation,
  useGetFileSyncDataRetrieverMutation,
  useDeviceRebootCommandMutation,
  useDeviceSetWifiCredentialsMutation,
  useGetListOfFilesCommandByDeviceIdMutation,
  useSendFileThroughEmailMutation,
  useGetDealerDeviceStateCountMutation,
  useDeleteDeviceCredentialsMutation,
  useDeleteScheduleFirmwareUpdateMutation
} = apiDevices;
